package com.trevorwiebe.timesheet.loads.domain

private const val PAGE_WIDTH = 612 // US Letter width in points

/**
 * Builds a PDF with one page per JPEG. The JPEG bytes are embedded as-is (DCTDecode), so there's
 * no re-encoding; each page is letter width with the height following the image's aspect ratio.
 */
fun jpegsToPdf(jpegs: List<ByteArray>): ByteArray {
    val chunks = mutableListOf<ByteArray>()
    var size = 0
    val objectOffsets = mutableListOf<Int>()

    fun write(bytes: ByteArray) {
        chunks += bytes
        size += bytes.size
    }
    fun write(text: String) = write(text.encodeToByteArray())
    fun startObject() {
        objectOffsets += size
        write("${objectOffsets.size} 0 obj\n")
    }

    // Object ids: 1 catalog, 2 page tree, then page / image / contents for each page.
    fun pageId(index: Int) = 3 + index * 3

    write("%PDF-1.4\n")
    startObject()
    write("<< /Type /Catalog /Pages 2 0 R >>\nendobj\n")
    startObject()
    val kids = jpegs.indices.joinToString(" ") { "${pageId(it)} 0 R" }
    write("<< /Type /Pages /Kids [$kids] /Count ${jpegs.size} >>\nendobj\n")

    jpegs.forEachIndexed { index, jpeg ->
        val info = jpegInfo(jpeg)
        val height = (PAGE_WIDTH.toLong() * info.height / info.width).toInt()
        val imageId = pageId(index) + 1
        val contentsId = pageId(index) + 2

        startObject()
        write(
            "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 $PAGE_WIDTH $height] " +
                "/Resources << /XObject << /Im0 $imageId 0 R >> >> /Contents $contentsId 0 R >>\nendobj\n"
        )

        startObject()
        write(
            "<< /Type /XObject /Subtype /Image /Width ${info.width} /Height ${info.height} " +
                "/ColorSpace /${info.colorSpace} /BitsPerComponent 8 /Filter /DCTDecode " +
                "/Length ${jpeg.size} >>\nstream\n"
        )
        write(jpeg)
        write("\nendstream\nendobj\n")

        val contents = "q $PAGE_WIDTH 0 0 $height 0 0 cm /Im0 Do Q"
        startObject()
        write("<< /Length ${contents.length} >>\nstream\n$contents\nendstream\nendobj\n")
    }

    val xrefOffset = size
    write("xref\n0 ${objectOffsets.size + 1}\n0000000000 65535 f \n")
    objectOffsets.forEach { write("${it.toString().padStart(10, '0')} 00000 n \n") }
    write("trailer\n<< /Size ${objectOffsets.size + 1} /Root 1 0 R >>\nstartxref\n$xrefOffset\n%%EOF\n")

    val pdf = ByteArray(size)
    var position = 0
    chunks.forEach {
        it.copyInto(pdf, position)
        position += it.size
    }
    return pdf
}

internal class JpegInfo(val width: Int, val height: Int, val colorSpace: String)

/** Reads the size and color components from the JPEG's start-of-frame segment. */
internal fun jpegInfo(jpeg: ByteArray): JpegInfo {
    fun byteAt(i: Int) = jpeg[i].toInt() and 0xFF
    fun shortAt(i: Int) = (byteAt(i) shl 8) or byteAt(i + 1)

    require(jpeg.size > 2 && byteAt(0) == 0xFF && byteAt(1) == 0xD8) { "Not a JPEG" }
    var i = 2
    while (i + 9 < jpeg.size) {
        if (byteAt(i) != 0xFF) {
            i++
            continue
        }
        val marker = byteAt(i + 1)
        if (marker == 0xFF) {
            i++
            continue
        }
        // SOF0–SOF15, excluding DHT (C4), JPG (C8) and DAC (CC), which share the range.
        if (marker in 0xC0..0xCF && marker != 0xC4 && marker != 0xC8 && marker != 0xCC) {
            val colorSpace = when (byteAt(i + 9)) {
                1 -> "DeviceGray"
                4 -> "DeviceCMYK"
                else -> "DeviceRGB"
            }
            return JpegInfo(width = shortAt(i + 7), height = shortAt(i + 5), colorSpace = colorSpace)
        }
        i += 2 + shortAt(i + 2)
    }
    throw IllegalArgumentException("JPEG has no frame header")
}
